import type { AxiosResponse } from 'axios';

import { api } from '#services/http-clients.ts';

export type PatientReadinessRunBucket = 'unconfirmed' | 'unreachable';

interface PatientReadinessHistory {
  isNewPatient: boolean;
  lateCancelCount: number;
  noShowCount: number;
  totalAppointmentsInWindow: number;
}

interface BasePatientReadinessDetailsPatient {
  userId: number;
  firstName?: string;
  lastName?: string;
}

interface UnconfirmedPatientReadinessDetailsPatient extends BasePatientReadinessDetailsPatient {
  history: PatientReadinessHistory;
}

type PatientReadinessDetailsPatient<TBucket extends PatientReadinessRunBucket> =
  TBucket extends 'unconfirmed'
    ? UnconfirmedPatientReadinessDetailsPatient
    : BasePatientReadinessDetailsPatient;

export interface PatientReadinessDetailsAppointment<TBucket extends PatientReadinessRunBucket> {
  id: number;
  startDate: string;
  confirmed: string | null;
  provider: {
    userId: number;
    firstName?: string;
    lastName?: string;
  };
  patient: PatientReadinessDetailsPatient<TBucket>;
  appointmentType: {
    id: number;
    name: string;
  };
}

export interface PatientReadinessRun {
  id: string;
  bucket: PatientReadinessRunBucket;
  runDate: string;
  windowStart: string;
  windowEnd: string;
  appointmentCount: number;
  appointmentIds: number[];
  createdAt: string;
  updatedAt: string;
}

export interface PatientReadinessDetailsSnapshotBucket<TBucket extends PatientReadinessRunBucket> {
  source: 'snapshot';
  run: PatientReadinessRun;
  data: PatientReadinessDetailsAppointment<TBucket>[];
}

export interface PatientReadinessDetailsCurrentBucket<TBucket extends PatientReadinessRunBucket> {
  source: 'current';
  run: null;
  data: PatientReadinessDetailsAppointment<TBucket>[];
}

export type PatientReadinessDetailsBucket<TBucket extends PatientReadinessRunBucket> =
  | PatientReadinessDetailsSnapshotBucket<TBucket>
  | PatientReadinessDetailsCurrentBucket<TBucket>;

export interface PatientReadinessDetailsResponse {
  unconfirmed: PatientReadinessDetailsBucket<'unconfirmed'>;
  unreachable: PatientReadinessDetailsBucket<'unreachable'>;
}

export function getPatientReadinessDetails(): Promise<PatientReadinessDetailsResponse> {
  return api
    .get<PatientReadinessDetailsResponse>('/engage/v1/patient-readiness/details', {
      suppressTokenUpdate: true,
      // Remove comment to test with local notification service
      // baseURL: '/',
    })
    .then((res) => {
      // Initial selection uses the returned order while the grid applies the same risk-first sort.
      // Keeping this at the fetch boundary avoids recalculating row order in the only consumer.
      return {
        ...res.data,
        unconfirmed: {
          ...res.data.unconfirmed,
          data: [...res.data.unconfirmed.data].sort((left, right) => {
            const leftHistory = left.patient.history;
            const rightHistory = right.patient.history;
            const leftAttendanceRate =
              leftHistory.totalAppointmentsInWindow === 0
                ? 0
                : (leftHistory.noShowCount + leftHistory.lateCancelCount) /
                  leftHistory.totalAppointmentsInWindow;
            const rightAttendanceRate =
              rightHistory.totalAppointmentsInWindow === 0
                ? 0
                : (rightHistory.noShowCount + rightHistory.lateCancelCount) /
                  rightHistory.totalAppointmentsInWindow;

            if (leftAttendanceRate !== rightAttendanceRate) {
              return rightAttendanceRate - leftAttendanceRate;
            }

            return new Date(left.startDate).getTime() - new Date(right.startDate).getTime();
          }),
        },
      };
    });
}

interface BaseCreatePatientReadinessRunRequest {
  asOf?: string; // Date in ISO format
}

interface CreatePatientReadinessUnconfirmedRunRequest extends BaseCreatePatientReadinessRunRequest {
  bucket: 'unconfirmed';
  appointmentIds: number[];
}

interface CreatePatientReadinessUnreachableRunRequest extends BaseCreatePatientReadinessRunRequest {
  bucket: 'unreachable';
}

type CreatePatientReadinessRunRequest =
  | CreatePatientReadinessUnconfirmedRunRequest
  | CreatePatientReadinessUnreachableRunRequest;

type CreatePatientReadinessRunResponse = {
  run: PatientReadinessRun;
};

export function createPatientReadinessRun(
  request: CreatePatientReadinessRunRequest
): Promise<AxiosResponse<CreatePatientReadinessRunResponse>> {
  return api.post<CreatePatientReadinessRunResponse>('/engage/v1/patient-readiness/runs', request, {
    // Remove comment to test with local notification service
    // baseURL: '/',
  });
}
